//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * @class am.SelectableLayerView
     * Subclass of L.VectorGrid.Protobuf whose elements can be selected and deselected.
     */
    module.SelectableLayerView = L.VectorGrid.Protobuf.extend({
        // Public properties
        options: { // default values of the class specific options
            defaultFeatureStyle: L.Path.prototype.options,
            selectedFeatureStyle: { // style which will be applied to the selected features
                color: 'rgb(0, 255, 255)',
            },
            disabledFeatureStyle: { // style which will be applied to the disabled features
                color: 'rgb(80, 80, 80)',
            },
        },

        // Private properties
        _selectedFeatureIds: new Set(),    // IDs of the selected features
        _highlightedFeatureIds: new Set(), // IDs of the highlighted features (i.e. non-dimed out features)
        _roofLayerName: undefined,         // Name of the (sub)layer containing the roofs in the tile layer

        /**
         * Class constructor
         * @constructor
         *
         * @param {String} url - Template of the URL at which the tiles are located (see 
         *      https://leaflet.github.io/Leaflet.VectorGrid/vectorgrid-api-docs.html#vectorgrid-protobuf) 
         * @param {String} idField - Name of the feature property which holds the feature ID
         * @param {String} roofLayerName - Name of the sublayer (in the vector tile layer) which contains the selectable
         *      (and highlightable) features
         * @param {Object} options - Same options as for class L.VectorGrid, with three additional possibility:
         *      - "defaultFeatureStyle": L.Path object defining the style which will be applied to all the features by 
         *          default (defaults to the features being colored in dark blue). This option replaces option 
         *          "vectorTileLayerStyles" expected by class L.VectorGrid.Protobuf.
         *      - "selectedFeatureStyle": L.Path object defining the style which will be applied to the selected features
         *          (defaults to selected features being colored in cyan)
         *      - "disabledFeatureStyle": L.Path object defining the style which will be applied to the disabled features
         *          (defaults to highlighted features being colored in light grey)
         */
        initialize: function(url, idField, roofLayerName, options) {
            // Impose some values of input argument 'options'
            options = options || {};
            options.getFeatureId = function(feature) {
                return feature.properties[idField];
            },
            options.interactive = true; // set the layer as being interactive

            // Call parent constructor
            L.VectorGrid.Protobuf.prototype.initialize.call(this, url, options);

            // Initialize the private properties
            this._roofLayerName = roofLayerName;

            // Define the default feature style
            this.options.vectorTileLayerStyles = this.options.vectorTileLayerStyles || {};
            this.options.vectorTileLayerStyles[roofLayerName] = this.options.defaultFeatureStyle;
        },

        /**
         * Activates the feature selection 
         */
        activateFeatureSelection: function() {
            this.options.interactive = true;
        },

        /**
         * Deactivates the feature selection
         */
        deactivateFeatureSelection: function() {
            this.options.interactive = false;
        },

        /**
         * Returns true if feature selection is activated
         *
         * @return {Boolean} - True if feature selection is activated, false otherwise
         */
        isFeatureSelectionActivated: function() {
            return this.options.interactive;
        },

        /**
         * Selects the feature whose ID is passed as input argument
         *
         * @param {(Number|String)} featureId - ID of the feature which is to be selected
         */
        selectFeature: function(featureId) {
            this.setFeatureStyle(featureId, this.options.selectedFeatureStyle);
            this._selectedFeatureIds.add(featureId);
        },

        /**
         * Selects the features whose IDs are passed as input argument
         *
         * @param {(Number[]|String[])} featureIds - Array containing the IDs of the features which are to be selected
         */
        selectFeatures: function(featureIds) {
            featureIds.forEach(function(featureId) {
                this.selectFeature(featureId);
            }, this);
        },

        /**
         * Returns the ID of the feature which is the nearest to the point passed as argument
         *
         * @param {L.LatLng} latLng - The feature closest to this point will be returned
         * @param {Number} threshold - If the nearest feature is further than this distance (specified in meters), it 
         *      will NOT be returned [defaults to Infinity]
         * @return {Number|String} - ID of the feature closest to the given point
         */
        getFeatureClosestToPoint: function(latLng, threshold) {
            threshold = threshold || Infinity;
            var layerPoint = this._map.latLngToLayerPoint(latLng);
            var minDistance = Infinity;
            var selectedFeatureId = undefined;
            this._onEachFeature(function(feature, tile) {
                var tilePoint = this._layerPointToTilePoint(layerPoint, tile);
                var closestFeaturePoint = feature.closestLayerPoint(tilePoint); // coordinates of the closest point in the tile reference system
                var distance = this._map.distance(latLng, this._tilePointToLatLng(closestFeaturePoint, tile));
                if (distance < Math.min(minDistance, threshold)) {
                    minDistance = distance;
                    selectedFeatureId = this.options.getFeatureId(feature);
                }
            }, this);

            return selectedFeatureId;
        },


        /**
         * Retunrs the IDs of the features which are (more or less) completely located inside the polygon passed as 
         * input argument. Note that, due to the simple approach used here, the code might consider a feature as being 
         * inside the polygon whereas it is actually NOT. Indeed, the code checks that all the vertices of the feature 
         * are inside the polygon, which is not sufficient for a concave feature.
         *
         * @param {L.Polygon} polygon - The polygon inside which the features should be selected
         * @return {String[]|Number[]} - IDs of the features inside the polygon
         */
        getFeaturesInPolygon: function(polygon) {
            var selectedFeatureIds = new Set();
            var ignoredFeatureIds  = new Set();       
            this._onEachFeature(function(feature, tile) {
                var featureId = this.options.getFeatureId(feature);
                var featureVertices = am.utils.flattenArray(feature._parts); // feature vertices in layer (= pixel) coordinates
                var isInPolygon = featureVertices.every(function(vertex) {
                    return polygon._containsPoint(this._tilePointToLayerPoint(vertex, tile)); // hack: private function _containsPoint() expects a point in layer coordinates
                });

                if (isInPolygon && !ignoredFeatureIds.has(featureId)) {
                    selectedFeatureIds.add(featureId);
                } else if (!isInPolygon) {
                    selectedFeatureIds.remove(featureId);
                    ignoredFeatureIds.add(featureId);
                }
            }, this);

            return selectedFeatureIds;
        },

        /**
         * Returns the IDs of the features which are completely or partially located inside the polygon passed as input 
         * argument
         *
         * @param {L.Polygon} polygon - The polygon inside which the features should be selected
         * @return {String[]|Number[]} - IDs of the features which are completely or partially located inside the polygon
         */
        getFeaturesInterectingPolygon: function(polygon) {
            var selectedFeatureIds = new Set();
            this._onEachFeature(function(feature, tile) {
                var featureId = this.options.getFeatureId(feature);
                var featureVertices = am.utils.flattenArray(feature._parts); // feature vertices in layer (= pixel) coordinates
                var intersectsPolygon = featureVertices.some(function(vertex) {
                    return polygon._containsPoint(this._tilePointToLayerPoint(vertex, tile)); // hack: private function _containsPoint() expects a point in layer coordinates
                });

                if (intersectsPolygon) {
                    selectedFeatureIds.add(featureId);
                }
            }, this);

            return selectedFeatureIds;
        },

        /**
         * Unselects the feature whose ID is passed as input argument
         *
         * @param {(Number|String)} featureId - ID of the feature which is to be deselected
         */
        unselectFeature: function(featureId) {
            this._selectedFeatureIds.delete(featureId);
            if (this._highlightedFeatureIds.has(featureId)) {
                this.setFeatureStyle(featureId, this.options.defaultFeatureStyle);
            } else {
                this.resetFeatureStyle(featureId);
            }
        },

        /**
         * Unselects the features whose IDs are passed as input argument
         *
         * @param {(Number[]|String[])} featureIds - IDs of the features which are to be deselected
         */
        unselectFeatures: function(featureIds) {
            featureIds.forEach(function(featureId) {
                this.deselectFeature(featureId);
            }, this);
        },

        /**
         * Unselects all the selected features
         */
        unselectAllFeatures: function() {
            this.unselectFeatures(this._selectedFeatureIds);
        },

        /**
         * All the features other than the one passed as input argument will be dimed out (i.e. displayed in light grey 
         * by default, see class options for more info).
         *
         * @param {Number[]|String[]} featureId - ID of the feature which sould be highlighted (i.e. not dimed out)
         */
        highlightFeature: function(featureId) {
            this.highlightFeatures([featureId]);
        },

        /**
         * Highlights the features whose IDs are passed as input arguments: all the features whose IDs are NOT passed
         * as input arguments will be dimed out (i.e. displayed in light grey by default, see class options for more 
         * info). This function resets the effects of a previous call to itself.
         *
         * @param {Number[]|String[]} featureIds - IDs of the features which sould be highlighted (i.e. not dimed out)
         */
        highlightFeatures: function(featureIds) {
            // Cancel previous call to the function
            this.removeHighlighting(); 

            // Override the default feature style with the disabled feature style (=> make all features disabled by
            // default)
            this.options.vectorTileLayerStyles[this._roofLayerName] = this.options.disabledFeatureStyle;

            // Store the IDs of the highlighted features and force them to keep their style
            featureIds.forEach(function(id) {
                this._highlightedFeatureIds.add(id);
                if (!this._selectedFeatureIds.has(id)) { // maintain feature style only if it is not currently selected
                    this.setFeatureStyle(id, this.options.defaultFeatureStyle);
                }
            }, this);

            // Update the style of the displayed features
            this.refresh();
        },

        /**
         * Restores the style of the features which have been dimed out by functions
         * am.view.SelectableLayer#highlightFeature or am.view.SelectableLayer#highlightFeatures
         */
        removeHighlighting: function() {
            // Restore the default feature style to its original value
            this.options.vectorTileLayerStyles[this._roofLayerName] = this.options.defaultFeatureStyle;

            // Remove the style of the highlighted features
            this._highlightedFeatureIds.forEach(function(id) {
                if (!this._selectedFeatureIds.has(id)) { // remove feature style only if it is not currently selected
                    this.resetFeatureStyle(id);
                }
            }, this);

            // Clear the list of highlighted features
            this._highlightedFeatureIds.clear();

            // Update the style of the displayed features
            this.refresh();
        },

        /**
         * Returns the IDs of the features for which the function passed as input argument evaluates to true
         * Important: This function only works for features which are located on loaded tiles!
         *
         * @param {Function} selector - A callback function which will be evaluated for each feature in the layer. This
         *      function should expect the feature as input argument and return true if the ID of the feature
         *      should be returned.
         * @return {Number[]|String[]} - Array of the features for which the input function evaluated to true
         */
        getFeatureIds: function(selector) {
            var ids = [];
            var testFeature = function(feature) {
                if (selector(feature)) {
                    ids.push(this.options.getFeatureId(feature));
                }
            };
            this._onEachFeature(testFeature, this);
            return ids;
        },

        /**
         * Returns the properties of the feature whose ID is passed as input argument
         * Important: This function only works for features which are located on loaded tiles!
         *
         * @param {String|Number} featureId - ID of the feature whose properties are to be returned
         * @return {Object[]} - Properties of the selected feature
         */
        getFeatureProperties: function(featureId) {
            for (var tileKey in this._vectorTiles) {
                var tile = this._vectorTiles[tileKey];
                var data = tile._features[featureId];
                if (data) {
                    return data.feature.properties;
                }
            }
        },

        /**
         * Returns the bounding box of the feature whose ID is passed as input argument
         * Important: This function only works for features which are located on loaded tiles!
         *
         * @param {String|Number} featureId - ID of the feature whose bounding box is to be returned
         * @return {L.LatLngBounds} - Bounding box of the selected feature
         */
        getFeatureBounds: function(featureId) {
            var bounds = undefined;

            for (var tileKey in this._vectorTiles) {
                var tile = this._vectorTiles[tileKey];
                var data = tile._features[featureId];
                if (data) {
                    var pxBounds = data.feature._pxBounds;
                    var llCorner = this._tilePointToLatLng(L.point(pxBounds.min.x, pxBounds.min.y), tile);
                    var urCorner = this._tilePointToLatLng(L.point(pxBounds.max.x, pxBounds.max.y), tile);
                    var latLngBounds = L.latLngBounds(llCorner, urCorner);
                    if (bounds == undefined) {
                        bounds = latLngBounds;
                    } else {
                        bounds.extend(latLngBounds); // extend feature bounds to contain the new feature part
                    }
                }
            }

            return bounds;
        },
        
        /**
         * Returns the centroid of the feature whose ID is passed as input argument
         * Important: This function only works for features which are located on loaded tiles!
         *
         * @param {String|Number} featureId - ID of the feature whose centroid is to be returned
         * @return {L.LatLng} - Centroid of the selected feature
         */
        getFeatureCentroid: function(featureId) {
            var bounds = this.getFeatureBounds(featureId);
            return (bounds == undefined) ? undefined : bounds.getCenter();
        },

        /**
         * Refreshes the style of the currently displayed features. This function should be called each time the style
         * of a feature is changed (except if this change is preformed thanks to L.VectorGrid.Protobuf#setFeatureStyle,
         * which handles itself the redrawing of the layer).
         */
        refresh: function() {
            for (var tileKey in this._vectorTiles) {
                var tile = this._vectorTiles[tileKey];
                var features = tile._layers; // hack: we iterate over property '_layers' and NOT '_features' because the later lacks the multipart features
                for (var id in features) {
                    var feature = features[id];
                    if (feature) {
                        var featureId = this.options.getFeatureId(feature);

                        var style = this._overriddenStyles[featureId];
                        if (style == undefined) {
                            style = this.options.vectorTileLayerStyles[this._roofLayerName];
                        }
                        if (style instanceof Function) {
                            style = style(feature.properties, tile.getCoord().z);
                        }

                        this._updateStyles(feature, tile, style);
                    }
                }
            }
        },

        /**
         * Calls the callback passed as input argument on each feature of the layer (note that a feature may be split 
         * between several tiles, in which case the callback will be called several times on the same feature: one for
         * each tile on which the feature is located)
         *
         * @param {Function} callback - Callback to be executed on each layer feature. The callback can accept two 
         *      input arguments: the feature and the tile in which it is located
         * @param {Object} thisObject - Object to use as 'this' in the callback function
         */
        _onEachFeature: function(callback, thisObject) {
            for (var tileKey in this._vectorTiles) {
                var tile = this._vectorTiles[tileKey]; // object of type L.SVG.Tile or L.Canvas.Tile (sublasses of L.SVG and L.Canvas, see vector_grid.js)
                var features = tile._features;
                for (var id in features) {
                    var data = features[id];
                    if (data) {
                        var feat = data.feature; // object of type L.PolygonLayer (subclass of L.Polygon, see vector_grid.js)
                        if (thisObject != undefined) {
                            callback.call(thisObject, feat, tile);
                        } else {
                            callback(feat, tile);
                        }
                    }
                }
            }
        },

        /**
         * Given a layer point (whose coordinates are defined with respect to the origin pixel, see 
         * http://leafletjs.com/examples/extending/extending-2-layers.html), returns its coordinates with respect
         * to the upper left corner of the tile passed as input argument.
         *
         * @param {L.Point} point - Point whose coordinates are expressed relative to the origin pixel
         * @param {Object} tile - The coordinates of returned point will be expressed with respect to the upper left 
         *      corner of this tile
         * @return {L.Point} - Coordinates of the point passed as input argument, expressed in the reference system of 
         *      the tile passed as input argument 
         */
        _layerPointToTilePoint: function(point, tile) {
            // Hack: the DOM element which contains all the tiles is offested with respect to the origin pixel. We 
            // therefore have to take this offset into account when converting the feature coordinates from the layer 
            // reference system (defined with respect to the origin pixel) to the tile reference system:
            // 
            //      coordinates [in the tile reference system] = coordinates [with respect to the origin pixel]
            //           - tile container coordinates [with respect to the origin pixel]
            //           - tile coordinates [in the tile container reference system]
            //
            var tileElement = tile._container; // DOM element corresponding to the tile
            var tileCoords  = L.DomUtil.getPosition(tileElement); // position of the upper left corner of the tile relative to the upper left corner of the tile container (in pixels)
            var tileContainerPos = $(tileElement.parentNode).position();
            var tileContainerCoords = L.point(tileContainerPos.left, tileContainerPos.top); // position of the upper left corner of the tile container relative to the origin pixel
            return point.subtract(tileContainerCoords).subtract(tileCoords);
        },

        /**
         * Inverse of _layerPointToTilePoint
         *
         * @param {L.Point} point - Point whose coordinates are expressed in the reference system of the tile passed as 
         *      second input argument 
         * @param {Object} tile - The coordinates of given input point are supposed to be expressed with respect to the 
         *      upper left corner of this tile
         * @return {L.Point} - Point whose coordinates are expressed with respect to the origin pixel 
         */
        _tilePointToLayerPoint: function(point, tile) {
            var tileElement = tile._container; // DOM element corresponding to the tile
            var tileCoords  = L.DomUtil.getPosition(tileElement); // position of the upper left corner of the tile relative to the upper left corner of the tile container (in pixels)
            var tileContainerPos = $(tileElement.parentNode).position();
            var tileContainerCoords = L.point(tileContainerPos.left, tileContainerPos.top); // position of the upper left corner of the tile container relative to the origin pixel
            return point.add(tileCoords).add(tileContainerCoords);
        },

        /**
         * Given an Earth point (whose coordinates are expressed in lat/lng), returns its coordinates with respect
         * to the upper left corner of the tile passed as input argument.
         *
         * @param {L.LatLng} latLng - Earth point
         * @param {Object} tile - The coordinates of returned point will be expressed with respect to the upper left 
         *      corner of this tile
         * @return {L.Point} - Coordinates of the point passed as input argument, expressed in the reference system of 
         *      the tile passed as input argument 
         */
        _latLngToTilePoint: function(latLng, tile) {
            var layerPoint = this._map.latLngToLayerPoint(latLng);
            return this._layerPointToTilePoint(layerPoint, tile);
        },

        /**
         * Inverse of _latLngToTilePoint
         *
         * @param {L.Point} point - Point whose coordinates are expressed in the reference system of the tile passed as 
         *      second input argument
         * @param {Object} tile - The coordinates of point passed as first input argument are supposed to be expressed 
         *      with respect to the upper left corner of this tile
         * @return {L.LatLng} - Coordinates of the point passed as input argument, expressed in the Earth's reference
         *      system (in lat/lng)
         */
        _tilePointToLatLng: function(point, tile) {
            var layerPoint = this._tilePointToLayerPoint(point, tile);
            return this._map.layerPointToLatLng(layerPoint);
        },
    });

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Factory method returning a new instance of class am.SelectableLayerView
     *
     * @param {String} url - Template of the URL at which the tiles are located (see 
     *      https://leaflet.github.io/Leaflet.VectorGrid/vectorgrid-api-docs.html#vectorgrid-protobuf) 
     * @param {String} idField - Name of the feature property which holds the feature ID
     * @param {String} roofLayerName - Name of the sublayer (in the vector tile layer) which contains the selectable
     *      (and highlightable) features
     * @param {Object} options - Options to be passed to the constructor of class am.SelectableLayerView
     * @return {am.SelectableLayerView} - New instance of class am.SelectableLayerView
     */
    module.selectableLayerView = function(url, idField, roofLayerName, options) {
        return new am.SelectableLayerView(url, idField, roofLayerName, options);
    };

    return module;

})(am.utils || {});